import { useContext, useEffect } from 'react';
import { Grid, Box, IconButton } from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { AppContext } from '../../sporttia/AppContext';
import {
    SttButton,
    SttDialog,
    SttFormCheck,
    SttFormSelect,
    SttFormText,
} from '../../sporttia/all';
import { useCrud } from '../../lib/hooks';
import { PermissionRequirement } from '../../lib/permissions';

const types = ['STRING', 'NUMERIC', 'DATE', 'SELECT', 'CHECK'] as const;

type QuestionType = (typeof types)[number];

type QuestionOption = {
    name: string;
    value: string;
};

type Question = {
    id?: number;
    name?: string;
    type?: QuestionType;
    mandatory?: number | boolean;
    options?: QuestionOption[];
};

type FormQuestionDialogProps = {
    parentCategory: string;
    idParent: number;
    item: Question | null;
    onSave?: (question: Question) => void;
    onDelete?: (question: Question) => void;
    onClose: () => void;
    /** Permiso v8 para guardar y borrar. Sin él, se pueden siempre. */
    permission?: PermissionRequirement;
};

type FormChangeEvent = {
    name: string | undefined;
    value: unknown;
};

type QuestionResponse = {
    question?: Question;
};

/**
 * FormQuestionDialog: Dialog to add or edit form questions
 */
export default function FormQuestionDialog({
    parentCategory,
    idParent,
    item,
    onSave,
    onDelete,
    onClose,
    permission,
}: FormQuestionDialogProps): React.ReactElement | null {
    const cxt = useContext(AppContext);
    const [question, setQuestion, setPropertyRaw, , Post, Put, Delete] =
        useCrud<Question>({} as Question);

    // Wrapper to adapt useCrud setProperty to form component onChange signature
    const setProperty = ({
        name,
        value,
    }: {
        name: string | undefined;
        value: unknown;
    }) => {
        if (!name) return;
        setPropertyRaw({
            name: name as keyof Question,
            value: value as Question[keyof Question],
        });
    };
    const { t } = useTranslation();

    useEffect(() => {
        if (item) {
            setQuestion(item);
        }
    }, [item]);

    function addOption(): void {
        const options = question?.options || [];
        setQuestion({
            ...question,
            options: options.concat([{ name: '', value: '' }]),
        } as Question);
    }

    function removeOption(option: QuestionOption): void {
        if (!question?.options) return;
        const options = [...question.options];
        options.splice(options.indexOf(option), 1);
        setQuestion({ ...question, options });
    }

    function setOption(
        option: QuestionOption,
        name: keyof QuestionOption,
        value: string,
    ): void {
        if (!question?.options) return;
        const options = [...question.options];
        const updatedOption = { ...option, [name]: value };
        options.splice(options.indexOf(option), 1, updatedOption);
        setQuestion({ ...question, options });
    }

    // Create/save question depending on parent (activity, event, etc) and parent id
    function saveQuestion(): void {
        if (!question) return;

        if (!question.type) {
            cxt?.showMessage('E', t('formQuestion.typeRequired'));
            return;
        }

        const params = {
            name: question.name,
            type: question.type,
            mandatory: !!question.mandatory,
            options:
                question.type === 'SELECT'
                    ? question.options?.map((option: any) => ({
                          name: option.name,
                          value: option.value,
                      }))
                    : null,
        };

        // Save existing question
        if (question.id) {
            Put<QuestionResponse>(`/questions/${question.id}`, params)
                .then(({ question: savedQuestion }) => {
                    if (savedQuestion && onSave) onSave(savedQuestion);
                    onClose();
                })
                .catch(console.log);
        } else {
            // Create new question, calling apis like /events/{id}/questions
            Post<QuestionResponse>(
                `/${parentCategory}/${idParent}/questions`,
                params,
            )
                .then(({ question: createdQuestion }) => {
                    if (createdQuestion && onSave) onSave(createdQuestion);
                    onClose();
                })
                .catch(console.log);
        }
    }

    function deleteQuestion(): void {
        if (!question?.id) return;

        Delete(`/questions/${question.id}`)
            .then(() => {
                if (onDelete) onDelete(question);
                onClose();
            })
            .catch(() => {});
    }

    if (!question) {
        return null;
    }

    return (
        <SttDialog
            maxWidth="sm"
            open={item !== null}
            title={t('Form')}
            onClose={onClose}
            buttons={
                [
                    {
                        type: 'save' as const,
                        onClick: saveQuestion,
                        permission,
                    },
                    question.id && {
                        type: 'delete' as const,
                        onClick: deleteQuestion,
                        permission,
                    },
                    {
                        type: 'close' as const,
                        onClick: onClose,
                    },
                ].filter(Boolean) as Parameters<typeof SttDialog>[0]['buttons']
            }
            content={
                <Grid container spacing={3}>
                    <Grid size={{ xs: 12 }}>
                        <SttFormText
                            caption={t('Question')}
                            name="name"
                            defVal={question.name ?? ''}
                            onChange={setProperty}
                        />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                        <SttFormSelect
                            caption={t('Type')}
                            name="type"
                            required
                            options={types.map((option: any) => ({
                                caption: t(`PT.${option}`),
                                value: option,
                            }))}
                            defVal={question.type ?? ''}
                            onChange={setProperty}
                        />
                    </Grid>
                    {question.type === 'SELECT' && (
                        <Grid size={{ xs: 12 }}>
                            <Box pl={1} pr={1}>
                                {question.options &&
                                    question.options.map((optionItem, i) => (
                                        <Box key={i} mb={2}>
                                            <Grid container spacing={0}>
                                                <Grid size={{ xs: 11 }}>
                                                    <Grid container spacing={2}>
                                                        <Grid
                                                            size={{
                                                                xs: 12,
                                                                sm: 6,
                                                            }}
                                                        >
                                                            <SttFormText
                                                                name="name"
                                                                caption={t(
                                                                    'Option',
                                                                )}
                                                                defVal={
                                                                    optionItem.name
                                                                }
                                                                onChange={({
                                                                    name,
                                                                    value,
                                                                }: FormChangeEvent) => {
                                                                    if (!name) {
                                                                        return;
                                                                    }
                                                                    setOption(
                                                                        optionItem,
                                                                        name as keyof QuestionOption,
                                                                        value as string,
                                                                    );
                                                                }}
                                                            />
                                                        </Grid>
                                                        <Grid
                                                            size={{
                                                                xs: 12,
                                                                sm: 6,
                                                            }}
                                                        >
                                                            <SttFormText
                                                                name="value"
                                                                caption={t(
                                                                    'Value',
                                                                )}
                                                                defVal={
                                                                    optionItem.value
                                                                }
                                                                onChange={({
                                                                    name,
                                                                    value,
                                                                }: FormChangeEvent) => {
                                                                    if (!name) {
                                                                        return;
                                                                    }
                                                                    setOption(
                                                                        optionItem,
                                                                        name as keyof QuestionOption,
                                                                        value as string,
                                                                    );
                                                                }}
                                                            />
                                                        </Grid>
                                                    </Grid>
                                                </Grid>
                                                <Grid size={{ xs: 1 }}>
                                                    <IconButton
                                                        style={{
                                                            width: 40,
                                                            height: 40,
                                                        }}
                                                        onClick={() =>
                                                            removeOption(
                                                                optionItem,
                                                            )
                                                        }
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </Grid>
                                            </Grid>
                                        </Box>
                                    ))}
                                <SttButton
                                    variant="contained"
                                    caption={t('AddOption')}
                                    onClick={addOption}
                                />
                            </Box>
                        </Grid>
                    )}
                    <Grid size={{ xs: 12 }}>
                        <SttFormCheck
                            name="mandatory"
                            caption={t('Mandatory')}
                            checked={!!question.mandatory}
                            onChange={({ name, value }: FormChangeEvent) => {
                                if (!name) return;
                                setProperty({
                                    name,
                                    value: +(value as number | boolean),
                                });
                            }}
                        />
                    </Grid>
                </Grid>
            }
        />
    );
}
